/**
 * `AbortSignal.reason` and `AbortSignal.throwIfAborted` do not exist in the React
 * Native app, so nothing compiled into native may call them directly.
 *
 * React Native installs the global `AbortController`/`AbortSignal` from the
 * `abort-controller` package (`react-native/Libraries/Core/setUpXHR.js`), which
 * predates both additions; Expo's winter runtime only adds the `timeout` and `any`
 * statics on top. Every browser has had them since 2022, so a direct call type-checks,
 * passes under jsdom and jest, works in the web app, and throws
 * `TypeError: signal.throwIfAborted is not a function` on device (BIG-8995).
 *
 * Patching the missing members onto the polyfill's prototype at app entry was considered
 * and rejected. It would cover this directory, `native/src` and our dependencies at once,
 * but it makes every caller's behaviour depend on an import order nothing enforces, and it
 * would leave native differing from web in a way no test on either runtime could show.
 * Wrappers are explicit and greppable, and they are enforced — by `abortSignal.guard.test.ts`
 * here, and by `no-restricted-syntax` in `native/eslint.config.js` for native's own code.
 */

/**
 * The reason a signal was aborted: its own `reason` where the runtime records one, and a
 * synthesised abort where it does not. Does not consult `signal.aborted`, so it answers for
 * a live signal and for no signal at all — call it only once you mean to cancel.
 *
 * React Native records no reason at all: its `abort()` takes no argument. So a reason a
 * caller passes to `controller.abort(...)` survives on web and is replaced by the
 * synthesised abort on device. Do not encode meaning there and expect to read it back.
 *
 * @param signal - The signal being cancelled, if there is one.
 * @returns The reason to reject with; never `undefined`.
 */
export function abortReason(signal?: AbortSignal): unknown {
  const reason = signal && "reason" in signal ? signal.reason : undefined;
  // Given `name: "AbortError"` because `isCancellation` matches by name, not by class.
  return reason ?? Object.assign(new Error("The operation was aborted."), { name: "AbortError" });
}

/** Stop here if the caller has already cancelled, as `signal.throwIfAborted()` would. */
export function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw abortReason(signal);
}
